import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Edit2 } from "lucide-react";

const validPhone = (v) => {
  const d = String(v ?? "").replace(/\D/g, "");
  return (d.length > 10 ? d.slice(-10) : d).length === 10;
};
const validPin = (v) => /^\d{6}$/.test(String(v ?? "").trim());

/**
 * Inline "Edit {pickup|drop} details" for the SELECTED location on the order-create
 * form — lets a user fix a not-manifest-ready location (phone, pincode, address…)
 * WITHOUT leaving the page. Saves to the shared location record (PUT) and calls
 * onSaved() so the parent can refetch. `kind` = "pickup" | "drop".
 */
export default function LocationQuickEdit({ kind, location, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  if (!location) return null;
  const isPickup = kind === "pickup";

  const openEditor = () => {
    setForm(isPickup ? {
      facility_name: location.facility_name || "",
      contact_person_name: location.contact_person_name || "",
      contact_phone: location.contact_phone || location.pickup_contact || "",
      address_line: location.address_line || "",
      city: location.city || "", state: location.state || "", pincode: location.pincode || "",
    } : {
      company_name: location.company_name || "",
      contact_person_name: location.contact_person_name || "",
      drop_contact: location.drop_contact || "",
      email: location.email || "",
      address_line: location.address_line || "",
      city: location.city || "", state: location.state || "", pincode: location.pincode || "",
    });
    setOpen(true);
  };

  const set = (f, v) => setForm((s) => ({ ...s, [f]: v }));
  const phoneVal = form ? (isPickup ? form.contact_phone : form.drop_contact) : "";

  const save = async () => {
    if (!validPhone(phoneVal)) { toast.error("Enter a valid 10-digit contact phone."); return; }
    if (!validPin(form.pincode)) { toast.error("Pincode must be 6 digits."); return; }
    setSaving(true);
    try {
      const path = isPickup ? "pickup-locations" : "drop-locations";
      // Pickup: keep pickup_contact (the manifest field) in sync with contact_phone.
      const payload = isPickup ? { ...form, pickup_contact: form.contact_phone } : { ...form };
      await axios.put(`${API}/${path}/${location.id}`, payload);
      toast.success(`${isPickup ? "Pickup" : "Drop"} location updated.`);
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update location");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="mt-1 inline-flex items-center gap-1 text-xs text-teal-600 hover:underline" onClick={openEditor}>
        <Edit2 className="w-3 h-3" /> Edit {kind} details
      </button>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
      <p className="rounded bg-amber-50 border border-amber-200 px-2 py-1 text-[11px] text-amber-800">
        Updates the saved {kind} location — it will also change on the {isPickup ? "Pickup" : "Drop"} Locations page and any other order using it.
      </p>
      {isPickup ? (
        <>
          <Input value={form.facility_name} onChange={(e) => set("facility_name", e.target.value)} placeholder="Facility name" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.contact_person_name} onChange={(e) => set("contact_person_name", e.target.value)} placeholder="Contact person" />
            <Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value.replace(/\D/g, "").slice(0, 10))} maxLength={10} inputMode="numeric" placeholder="Phone (10-digit)" />
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Company name" />
            <Input value={form.contact_person_name} onChange={(e) => set("contact_person_name", e.target.value)} placeholder="Contact person" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.drop_contact} onChange={(e) => set("drop_contact", e.target.value.replace(/\D/g, "").slice(0, 10))} maxLength={10} inputMode="numeric" placeholder="Phone (10-digit)" />
            <Input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email (optional)" />
          </div>
        </>
      )}
      <Input value={form.address_line} onChange={(e) => set("address_line", e.target.value)} placeholder="Address" />
      <div className="grid grid-cols-3 gap-2">
        <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="City" />
        <Input value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="State" />
        <Input value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} inputMode="numeric" placeholder="Pincode" />
      </div>
      {phoneVal && !validPhone(phoneVal) && <p className="text-xs text-red-600">Phone must be 10 digits.</p>}
      {form.pincode && !validPin(form.pincode) && <p className="text-xs text-red-600">Pincode must be 6 digits.</p>}
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
        <Button type="button" size="sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}
